import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Card } from '@/components/ui/card.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Q_EMPLEADO_BY_ID, Q_EMPLEADOS } from '../graphql/queries.js';
import { M_CREATE_EMPLEADO, M_UPDATE_EMPLEADO } from '../graphql/mutations.js';
import { useToasts } from '../hooks/useToasts.js';
import { subscribeToApolloErrors } from '@/apollo/client.js';
import EmpleadoForm from '../components/EmpleadoForm.jsx';
import ToastStack from '../components/ToastStack.jsx';

// Página de alta/edición de empleados con formulario validado
export function EmpleadoFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { toasts, addToast, removeToast } = useToasts();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToApolloErrors(payload => addToast(payload));
    return unsubscribe;
  }, [addToast]);

  const { data, loading } = useQuery(Q_EMPLEADO_BY_ID, {
    skip: !isEditMode,
    variables: { id },
  });

  const [createEmpleado] = useMutation(M_CREATE_EMPLEADO, {
    refetchQueries: [{ query: Q_EMPLEADOS }],
    onCompleted: result => {
      addToast({ type: 'success', message: `Empleado ${result.createEmpleado.nombre} creado.` });
      navigate('/rrhh/empleados');
    },
    onError: () => addToast({ type: 'error', message: 'No pudimos crear el empleado.' }),
    // Alternativa: cache.modify para inyectar el nodo en la lista actual.
  });

  const [updateEmpleado] = useMutation(M_UPDATE_EMPLEADO, {
    refetchQueries: [{ query: Q_EMPLEADOS }],
    onCompleted: result => {
      addToast({ type: 'success', message: `Empleado ${result.updateEmpleado.nombre} actualizado.` });
      navigate('/rrhh/empleados');
    },
    onError: () => addToast({ type: 'error', message: 'No pudimos actualizar el empleado.' }),
  });

  const defaultValues = useMemo(() => {
    if (!isEditMode) {
      return {
        nombre: '',
        apellido: '',
        empresaId: '',
        estado: 'ACTIVO',
      };
    }
    if (!data?.empleadoById) {
      return {
        nombre: '',
        apellido: '',
        empresaId: '',
        estado: 'ACTIVO',
      };
    }
    const empleado = data.empleadoById;
    return {
      ...empleado,
      salario: empleado.salario ?? '',
      fechaIngreso: empleado.fechaIngreso ? empleado.fechaIngreso.slice(0, 10) : '',
      fechaNacimiento: empleado.fechaNacimiento ? empleado.fechaNacimiento.slice(0, 10) : '',
    };
  }, [data, isEditMode]);

  const handleSubmit = async formData => {
    const payload = Object.entries(formData).reduce((acc, [key, value]) => {
      if (value === '' || value == null) {
        return acc;
      }
      if (key === 'salario') {
        acc.salario = Number(value);
        return acc;
      }
      if (key === 'fechaIngreso' || key === 'fechaNacimiento') {
        acc[key] = new Date(value).toISOString();
        return acc;
      }
      acc[key] = value;
      return acc;
    }, {});

    setIsSubmitting(true);
    try {
      if (isEditMode) {
        await updateEmpleado({
          variables: { id, input: payload },
          optimisticResponse: {
            updateEmpleado: {
              __typename: 'Empleado',
              id,
              nombre: payload.nombre || '',
              apellido: payload.apellido || '',
              legajo: payload.legajo || null,
              estado: payload.estado || 'ACTIVO',
            },
          },
        });
      } else {
        await createEmpleado({
          variables: { input: payload },
          optimisticResponse: {
            createEmpleado: {
              __typename: 'Empleado',
              id: 'temp-id',
              nombre: payload.nombre || '',
              apellido: payload.apellido || '',
              legajo: payload.legajo || null,
              estado: payload.estado || 'ACTIVO',
            },
          },
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      <ToastStack toasts={toasts} onDismiss={removeToast} />
      <header className="flex flex-col gap-2">
        <Button variant="ghost" className="w-fit rounded-2xl" onClick={() => navigate('/rrhh/empleados')}>
          ← Volver al listado
        </Button>
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">
          {isEditMode ? 'Editar empleado' : 'Nuevo empleado'}
        </h1>
        <p className="text-sm text-[var(--text-muted)]">
          Completa los campos requeridos para mantener el legajo actualizado.
        </p>
      </header>
      <Card className="rounded-3xl border border-[var(--border)] bg-[var(--panel)] p-8 shadow-[var(--shadow-soft)]">
        {loading && isEditMode ? (
          <p className="text-sm text-[var(--text-muted)]">Cargando información del empleado...</p>
        ) : (
          <EmpleadoForm defaultValues={defaultValues} onSubmit={handleSubmit} submitting={isSubmitting} />
        )}
      </Card>
    </div>
  );
}

export default EmpleadoFormPage;
